import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Autocomplete, Box, Button, Checkbox, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, MenuItem, TextField, Typography,
} from "@mui/material";
import TransferService from "../services/TransferService";
import Labels from "../labels/Labels";
import UserService from "../services/UserService";

export default function SvbTransferResolutionDialog({ row, action, employerId, dates, onClose }) {
  const [reason, setReason] = useState("");
  const [mappings, setMappings] = useState({});
  const [confirmed, setConfirmed] = useState(false);
  const [entryTypes, setEntryTypes] = useState({});
  const [feeSelections, setFeeSelections] = useState({});
  const [feeMonths, setFeeMonths] = useState({});
  const defaultFeeMonth = row.asOfDate?.slice(0, 7) || "";
  const queryClient = useQueryClient();
  const mapping = action === "MAPPED";
  const studies = useQuery(
    ["svb-study-options", employerId],
    () => TransferService.fetchSvbStudies(employerId),
    { enabled: mapping, retry: false }
  );
  const hasFees = Object.values(entryTypes).includes("FEE");
  const hasStudies = (row.studies || []).some((_entry, index) => entryTypes[index] !== "FEE");
  const feeTypes = useQuery(
    ["feeTypes"],
    () => TransferService.fetchSvbFeeTypes(),
    { enabled: mapping && hasFees, retry: false }
  );
  const mutation = useMutation(
    () => TransferService.resolveSvbTransfer({
      idEmployer: employerId,
      from: dates.from,
      to: dates.to,
      transactionId: row.id,
      fingerprint: row.fingerprint,
      action,
      reason: reason.trim(),
      mappings: mapping
        ? row.studies.map((_study, entryIndex) => entryTypes[entryIndex] === "FEE"
          ? { entryIndex, feeTypeId: feeSelections[entryIndex]?.id,
            feeMonth: feeMonths[entryIndex] ?? defaultFeeMonth }
          : { entryIndex, studyId: mappings[entryIndex]?.id })
        : [],
    }),
    {
      retry: false,
      onSuccess: () => {
        queryClient.invalidateQueries(["svb-transfers"]);
        queryClient.invalidateQueries(["transfers"]);
        queryClient.invalidateQueries(["administrativeFees"]);
        onClose();
      },
    }
  );
  const selected = (row.studies || []).flatMap((_entry, index) =>
    entryTypes[index] !== "FEE" && mappings[index] ? [mappings[index].id] : []);
  const duplicate = new Set(selected).size !== selected.length;
  const complete = !!row.studies?.length && row.studies.every((_entry, index) =>
    entryTypes[index] === "FEE"
      ? !!feeSelections[index] && /^[0-9]{4}-(0[1-9]|1[0-2])$/.test(feeMonths[index] ?? defaultFeeMonth)
        && !(feeMonths[index] ?? defaultFeeMonth).startsWith("0000")
      : !!mappings[index]);
  const canSave = confirmed && !!reason.trim() && !mutation.isLoading
    && (!mapping || (complete && !duplicate
      && (!hasStudies || (!studies.isError && !studies.isFetching))
      && (!hasFees || (!feeTypes.isError && !feeTypes.isFetching))));
  const errorMessage = (error) => error?.response?.data?.description || error?.message
    || Labels.getLabel("queryTransfers.svb.resolveError", "Unable to resolve this transaction. Refresh before retrying.");

  return (
    <Dialog open onClose={mutation.isLoading ? undefined : onClose} fullWidth maxWidth="md"
      aria-labelledby="svb-resolution-title">
      <DialogTitle id="svb-resolution-title">
        {mapping
          ? Labels.getLabel("queryTransfers.svb.mapTitle", "Map studies & create transfers")
          : Labels.getLabel("queryTransfers.svb.manualTitle", "Mark MANUAL PROCESSED")}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2, overflowWrap: "anywhere" }}>
          {row.id} — {row.amount} {row.currency}
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {mapping
            ? Labels.getLabel("queryTransfers.svb.mapWarning",
              "Assign every entry to a study or administrative fee. Study destinations must be different. Amounts cannot be changed and must total the bank amount. Study transfers follow auto-confirm settings. Fees create records only, using the selected employer and the fee month you select.")
            : Labels.getLabel("queryTransfers.svb.manualWarning",
              "This records an audit marker only. No transfers or funds will be created. The scheduler will not import this transaction later.")}
        </Alert>
        {mapping && hasFees && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {Labels.getLabel("queryTransfers.svb.feeContext", "Administrative fee company / bank date")}: {employerId} / {row.asOfDate}
          </Typography>
        )}
        {mapping && hasFees && feeTypes.isError && (
          <Alert severity="error" action={<Button onClick={() => feeTypes.refetch()}>
            {Labels.getLabel("queryTransfers.svb.retry", "Retry")}
          </Button>}>{errorMessage(feeTypes.error)}</Alert>
        )}
        {mapping && studies.isError && (
          <Alert severity="error" action={<Button onClick={() => studies.refetch()}>
            {Labels.getLabel("queryTransfers.svb.retry", "Retry")}
          </Button>}>{errorMessage(studies.error)}</Alert>
        )}
        {mapping && !studies.isFetching && !studies.isError && !(studies.data?.data || []).length && (
          <Alert severity="info">{Labels.getLabel("queryTransfers.svb.noStudies", "No accessible active studies are available for this employer.")}</Alert>
        )}
        {mapping && (row.studies || []).map((entry, index) => (
          <Box key={index} sx={{ my: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {index + 1}. {entry.invoiceNumber} — {entry.studyId} — {entry.amount} {row.currency}
            </Typography>
            <TextField select size="small" fullWidth sx={{ mb: 1 }}
              label={`${Labels.getLabel("queryTransfers.svb.entryType", "Entry type")} ${index + 1}`}
              value={entryTypes[index] || "STUDY"} disabled={mutation.isLoading}
              onChange={(event) => setEntryTypes((current) => ({ ...current, [index]: event.target.value }))}>
              <MenuItem value="STUDY">{Labels.getLabel("queryTransfers.svb.study", "Study")}</MenuItem>
              {UserService.hasRole(["config-admin-fees"]) && (
                <MenuItem value="FEE">{Labels.getLabel("queryTransfers.svb.administrativeFee", "Administrative fee")}</MenuItem>
              )}
            </TextField>
            {entryTypes[index] === "FEE" ? (
              <Box>
              <Autocomplete
                options={feeTypes.data?.data || []}
                loading={feeTypes.isFetching}
                value={feeSelections[index] || null}
                disabled={mutation.isLoading}
                onChange={(_event, fee) => setFeeSelections((current) => ({ ...current, [index]: fee }))}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(fee) => `${fee.description || fee.id} [${fee.id}]`}
                renderInput={(params) => <TextField {...params} size="small"
                  label={`${Labels.getLabel("administrativeFee.feeType", "Fee type")} ${index + 1}`} />}
              />
              <TextField type="month" size="small" required sx={{ mt: 1 }}
                label={`${Labels.getLabel("queryTransfers.svb.feeMonth", "Fee month")} ${index + 1}`}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: "0001-01", max: "9999-12" }}
                value={feeMonths[index] ?? defaultFeeMonth}
                disabled={mutation.isLoading}
                onChange={(event) => setFeeMonths((current) => ({ ...current, [index]: event.target.value }))} />
              </Box>
            ) : (
            <Autocomplete
              options={studies.data?.data || []}
              loading={studies.isFetching}
              value={mappings[index] || null}
              disabled={mutation.isLoading}
              onChange={(_event, study) => setMappings((current) => ({ ...current, [index]: study }))}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(study) => `${study.name || study.id} [${study.id}]${study.autoConfirm ? " — AUTO_CONFIRMED" : " — REQUESTED"}`}
              renderInput={(params) => <TextField {...params} size="small"
                label={`${Labels.getLabel("queryTransfers.svb.destinationStudy", "Destination study")} ${index + 1}`} />}
            />
            )}
          </Box>
        ))}
        {mapping && duplicate && (
          <Alert severity="error">{Labels.getLabel("queryTransfers.svb.uniqueStudies", "Each entry must map to a different real study.")}</Alert>
        )}
        <TextField label={Labels.getLabel("queryTransfers.svb.reason", "Audit reason")}
          fullWidth multiline minRows={2} sx={{ mt: 2 }} value={reason}
          disabled={mutation.isLoading} inputProps={{ maxLength: 1000 }}
          onChange={(event) => setReason(event.target.value)} required />
        <FormControlLabel
          control={<Checkbox checked={confirmed} disabled={mutation.isLoading}
            onChange={(event) => setConfirmed(event.target.checked)} />}
          label={Labels.getLabel("queryTransfers.svb.confirmResolution", "I have reviewed this transaction and confirm this action.")}
        />
        {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button disabled={mutation.isLoading} onClick={onClose}>{Labels.getLabel("queryTransfers.svb.cancel", "Cancel")}</Button>
        <Button variant="contained" disabled={!canSave} onClick={() => mutation.mutate()}>
          {mapping
            ? Labels.getLabel("queryTransfers.svb.createTransfers", "Create transfers")
            : Labels.getLabel("queryTransfers.svb.markManual", "Mark MANUAL PROCESSED")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
